/**
 * Pal Builder — pure helpers and constants.
 *
 * Lives outside Builder.jsx so the page stays under the team's
 * 700-line guideline and so the same constants can be reused by
 * future builder-adjacent UIs (e.g. a Lite/Mobile builder) without
 * dragging the React surface area along.
 *
 * Anything in this file MUST be:
 *   - pure (no React, no DOM)
 *   - deterministic (no time/randomness)
 *   - data-shape-stable (the Pal Builder page imports these by name)
 *
 * If a helper grows React/JSX, move it to its own component file
 * under /app/frontend/src/components/builder/.
 */

// ─────────────────────────────────────────────────────────────────────
// Numeric caps — mirrored in backend `pal_spawn.py`.
//
// IVs and Souls have two ceilings: vanilla-legal (default) and the
// modded extended range that PalDefender supports natively. The
// Builder page resolves which to apply based on the selected delivery
// target's `modded` flag (`targetIsModded`).
// ─────────────────────────────────────────────────────────────────────
export const MAX_ACTIVE = 3;
export const MAX_PASSIVE = 4;
export const MAX_LEVEL = 80;        // vanilla Palworld cap (1.0 raised from 55 → 80)
export const MAX_LEVEL_MODDED = 255; // PalDefender mod cap
export const HIGH_LEVEL_SURCHARGE = 2500; // coin surcharge for Lv > 80
export const MAX_IV = 100;
export const MAX_IV_MODDED = 255;   // PalDefender extended IV range
export const MAX_SOUL = 20;         // condenser per-stat cap
export const MAX_SOUL_MODDED = 1020; // PalDefender extended PalSouls range
export const MAX_RANK = 4;          // condenser stars
export const MAX_TRUST = 10;
export const MAX_WORK_LEVEL = 10;   // PalDefender clamp on ExtraWorkSuitabilities (raised 5 → 10 per the updated 2026-07 template)

// ─────────────────────────────────────────────────────────────────────
// Work Suitabilities — 12 keys PalDefender recognises in active gameplay.
// `OilExtraction` was removed by Pocketpair so it's intentionally absent.
// Must mirror `WORK_SUIT_KEYS` in backend/api/routes/pal_spawn.py.
// Each entry's `iconName` is a Lucide icon — the page imports them at
// the call site to avoid pulling the whole icon set here.
// ─────────────────────────────────────────────────────────────────────
export const WORK_SUITS = [
  { key: "EmitFlame",            label: "Kindling",       iconName: "Flame"     },
  { key: "Watering",             label: "Watering",       iconName: "Droplets"  },
  { key: "Seeding",              label: "Planting",       iconName: "Sprout"    },
  { key: "GenerateElectricity",  label: "Electricity",    iconName: "Zap"       },
  { key: "Handcraft",            label: "Handiwork",      iconName: "Hammer"    },
  { key: "Collection",           label: "Gathering",      iconName: "Apple"     },
  { key: "Deforest",             label: "Lumbering",      iconName: "Trees"     },
  { key: "Mining",               label: "Mining",         iconName: "Pickaxe"   },
  { key: "ProductMedicine",      label: "Medicine Prod.", iconName: "Pill"      },
  { key: "Cool",                 label: "Cooling",        iconName: "Snowflake" },
  { key: "Transport",            label: "Transporting",   iconName: "Truck"     },
  { key: "MonsterFarm",          label: "Farming",        iconName: "Beef"      },
];
export const EMPTY_WORK_SUITS = Object.fromEntries(WORK_SUITS.map((w) => [w.key, 0]));
export const MAX_WORK_SUITS   = Object.fromEntries(WORK_SUITS.map((w) => [w.key, MAX_WORK_LEVEL]));

// ─────────────────────────────────────────────────────────────────────
// paldex's WorkSuitability dict uses display names ("Handiwork",
// "Mining", "Transporting", …). Translate them to PalDefender keys so
// the editor can pre-fill from /api/paldex/<slug>.
// Verified against gamedata/itemdata.json (Applied ___ Technique).
// ─────────────────────────────────────────────────────────────────────
export const PALDEX_WORK_NAME_TO_KEY = {
  "Kindling":               "EmitFlame",
  "Watering":               "Watering",
  "Planting":               "Seeding",
  "Electricity":            "GenerateElectricity",
  "Generating Electricity": "GenerateElectricity",
  "Handiwork":              "Handcraft",
  "Gathering":              "Collection",
  "Lumbering":              "Deforest",
  "Mining":                 "Mining",
  "Medicine Production":    "ProductMedicine",
  "Cooling":                "Cool",
  "Transporting":           "Transport",
  "Farming":                "MonsterFarm",
};

/**
 * Convert a paldex `WorkSuitability` block to the 12-key bonus block.
 * Accepts BOTH shapes: {"Mining": 3} dicts and the V8 new-pal list shape
 * [{work_suit_type: "Mining", rank: 3}]. Unknown names ignored; clamped.
 */
export function naturalWorkSuitsFromPaldex(pdWorkSuitability) {
  const out = { ...EMPTY_WORK_SUITS };
  let entries = [];
  if (Array.isArray(pdWorkSuitability)) {
    entries = pdWorkSuitability.map((e) => [e?.work_suit_type, e?.rank]);
  } else if (pdWorkSuitability && typeof pdWorkSuitability === "object") {
    entries = Object.entries(pdWorkSuitability);
  }
  for (const [displayName, lvl] of entries) {
    const key = PALDEX_WORK_NAME_TO_KEY[displayName];
    if (key) out[key] = Math.max(0, Math.min(MAX_WORK_LEVEL, Number(lvl) || 0));
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────
// Workforce one-click presets — bias a pal toward a base-camp role.
// `tags` = WORK_SUITS keys that should be set to MAX_WORK_LEVEL; the
// rest snap back to 0 (replace, not merge).
// ─────────────────────────────────────────────────────────────────────
export const WORK_PRESETS = [
  { id: "builder", label: "🏗 Builder", tags: ["Handcraft", "Mining", "Transport"] },
  { id: "kitchen", label: "🍳 Kitchen", tags: ["Watering", "Collection", "Cool"] },
  { id: "ranch",   label: "🌾 Ranch",   tags: ["Seeding", "MonsterFarm", "Watering"] },
  { id: "miner",   label: "⛏ Miner",    tags: ["Mining", "Transport", "EmitFlame"] },
  { id: "lumber",  label: "🪓 Lumber",  tags: ["Deforest", "Transport", "Handcraft"] },
  { id: "factory", label: "⚡ Factory", tags: ["GenerateElectricity", "Handcraft", "Transport"] },
  { id: "medic",   label: "💊 Medic",   tags: ["ProductMedicine", "Cool", "Watering"] },
];

export function presetWorkSuits(presetId) {
  const preset = WORK_PRESETS.find((p) => p.id === presetId);
  if (!preset) return EMPTY_WORK_SUITS;
  return Object.fromEntries(WORK_SUITS.map((w) => [
    w.key,
    preset.tags.includes(w.key) ? MAX_WORK_LEVEL : 0,
  ]));
}

// ─────────────────────────────────────────────────────────────────────
// Combat / loadout passive presets. Every asset listed is UNIVERSAL —
// verified against gamedata/pal_passives.json (no `pals`/`restrict`
// field). Species-locked passives (Lord_*, Witch, Earth Empress, etc.)
// are deliberately excluded so the presets are safe for every pal.
// ─────────────────────────────────────────────────────────────────────
export const COMBAT_PRESETS = [
  { id: "cannon",
    label: "🔥 Glass Cannon",
    assets: ["Noukin", "PAL_ALLAttack_up3", "Legend", "Rare"] },
  { id: "tank",
    label: "🛡 Tank",
    assets: ["Deffence_up3", "Vampire", "Legend", "PAL_Sanity_Down_2"] },
  { id: "speedster",
    label: "💨 Speedster",
    assets: ["MoveSpeed_up_3", "Legend", "Rare", "PAL_Sanity_Down_2"] },
  { id: "workhorse",
    label: "⚒ Workhorse",
    assets: ["CraftSpeed_up3", "PAL_Sanity_Down_2", "Rare", "Legend"] },
];

/**
 * Resolve a preset's asset list against the loaded passive skill DB
 * to full {name, asset, rank, icon} objects. Missing assets are
 * silently dropped so a stale asset name never breaks the click.
 *
 * @param {string} presetId   one of COMBAT_PRESETS[].id
 * @param {Array}  passiveSkills  output of /api/skills/builder/passive
 * @returns {Array}           up to MAX_PASSIVE passive objects
 */
export function resolvePresetPassives(presetId, passiveSkills) {
  const preset = COMBAT_PRESETS.find((p) => p.id === presetId);
  if (!preset) return [];
  return preset.assets
    .map((a) => passiveSkills.find((p) => p.asset === a))
    .filter(Boolean)
    .slice(0, MAX_PASSIVE);
}
